import EcsTracking from 'mc-ecs-event-emitter';
import { generateRandomString } from '@mc/fn/rando';
import { createTrackingEvent } from '@mc/mc-ecs-storage/DynamicEvents/Surveys/trackingMetadata';

import type {
  RangeType,
  RangeLabel,
  RangePresentation,
  SurveyResponseType,
  ECSEvent,
} from '../types';

const QUESTION_RESPONSE_TYPES: SurveyResponseType[] = [
  'pickOne',
  'pickMany',
  'email',
  'range',
  'dropdown',
  'text',
  'contactInformation',
];

export const isQuestionResponse = (value: SurveyResponseType) => {
  return QUESTION_RESPONSE_TYPES.includes(value);
};

export const generateUniqueValue = (knownValues: string[]) => {
  let value;

  do {
    value = generateRandomString();
  } while (knownValues.indexOf(value) >= 0);

  return value;
};

export const rangeTypes: RangeType[] = [
  {
    lowRangeValue: 0,
    highRangeValue: 5,
    rangeLabel: '0 - 5',
    rangePresentation: 'number',
  },
  {
    lowRangeValue: 1,
    highRangeValue: 5,
    rangeLabel: '1 - 5',
    rangePresentation: 'number',
  },
  {
    lowRangeValue: 0,
    highRangeValue: 7,
    rangeLabel: '0 - 7',
    rangePresentation: 'number',
  },
  {
    lowRangeValue: 1,
    highRangeValue: 7,
    rangeLabel: '1 - 7',
    rangePresentation: 'number',
  },
  {
    lowRangeValue: 0,
    highRangeValue: 10,
    rangeLabel: '0 - 10',
    rangePresentation: 'number',
  },
  {
    lowRangeValue: 1,
    highRangeValue: 10,
    rangeLabel: '1 - 10',
    rangePresentation: 'number',
  },
  {
    lowRangeValue: 1,
    highRangeValue: 5,
    rangeLabel: '⭐⭐⭐⭐⭐',
    rangePresentation: 'star',
  },
  {
    lowRangeValue: 1,
    highRangeValue: 5,
    rangeLabel: '😫 🙁 😐 🙂 😁',
    rangePresentation: 'smiley',
  },
  {
    lowRangeValue: 1,
    highRangeValue: 5,
    rangeLabel: '❤️❤️❤️❤️❤️',
    rangePresentation: 'heart',
  },
];

export const getRangeTypeFromLabel = (label: RangeLabel) => {
  return rangeTypes.find(({ rangeLabel }) => rangeLabel === label);
};

export const getRangeLabel = (
  minRangeValue = 0,
  maxRangeValue = 10,
  presentation = 'number',
) => {
  return rangeTypes.find(
    ({ lowRangeValue, highRangeValue, rangePresentation }) => {
      return (
        lowRangeValue === minRangeValue &&
        highRangeValue === maxRangeValue &&
        rangePresentation === presentation
      );
    },
  )?.rangeLabel;
};

export const getRangeIncrements = (
  lowRangeValue: number,
  highRangeValue: number,
) => {
  return [...Array(highRangeValue - lowRangeValue + 1).keys()].map(
    (num) => num + lowRangeValue,
  );
};

export const getEmojiDisplayValues = (
  emoji: string,
  lowRangeValue: number,
  highRangeValue: number,
) => {
  let prefix = '';
  return new Array(highRangeValue - lowRangeValue + 1).fill('').map(() => {
    prefix = `${prefix}${emoji}`;
    return prefix;
  });
};

export const getRangeDisplayValues = (
  presentation: RangePresentation,
  lowRangeValue: number,
  highRangeValue: number,
) => {
  switch (presentation) {
    case 'number':
      return getRangeIncrements(lowRangeValue, highRangeValue).map(String);
    case 'star':
      return getEmojiDisplayValues('⭐', lowRangeValue, highRangeValue);
    case 'heart':
      return getEmojiDisplayValues('❤️', lowRangeValue, highRangeValue);
    case 'smiley':
      return ['😫', '🙁', '😐', '🙂', '😁'];
    default:
      throw new Error('Range presentation not recognized');
  }
};

export const RANGE_CONDITIONS = ['==', '!=', '>', '>=', '<', '<='];

export const SURVEY_CLOSED_MESSAGE =
  'This survey is no longer accepting responses.';
export const MISSING_ANSWER_DEFAULT = 'Question skipped';

type SurveyTrackProperties = {
  survey_id?: number;
  [key: string]: string | number | boolean | undefined;
};

/**
 * Survey dynamic events sent through `createTrackingEvent` (Surveys trackingMetadata) are
 * constrained by the Authoring Tool / tracking plan to string and number property values.
 * Boolean flags are encoded as 1 (true) and 0 (false) so they stay numeric in Segment payloads.
 */
export const encodeSurveyDynamicEventProperties = (
  properties: SurveyTrackProperties,
): {
  survey_id?: number;
  [key: string]: string | number | undefined;
} => {
  const encoded: {
    survey_id?: number;
    [key: string]: string | number | undefined;
  } = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) {
      continue;
    }
    if (typeof value === 'boolean') {
      encoded[key] = value ? 1 : 0;
    } else {
      encoded[key] = value;
    }
  }
  return encoded;
};

export const track = (
  event: ECSEvent | string | undefined,
  additionalProperties: SurveyTrackProperties = {},
) => {
  if (!event) {
    return;
  }

  // Authoring tool way of tracking events
  if (typeof event === 'string') {
    createTrackingEvent(
      event,
      encodeSurveyDynamicEventProperties(additionalProperties),
    );
    return;
  }

  // Old method of ECS events
  EcsTracking.track(event, additionalProperties);
};
